import React, { useState, useEffect } from 'react';
import { Button, message, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import AccountFormDrawer from './components/AccountFormDrawer';
import FilterArea, { DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import { GET_ACCOUNT_LISTING_FOR_WAREHOUSE, GetAccountListingForWarehouseResponse } from './graphql/queries/getAccountListingForWarehouse';
import { GET_HUB_DSP_MANAGERS, GetHubDspManagersResponse } from '../../central/settings/components/graphql/queries/getHubDspManagers';
import { useQuery } from '@apollo/client';
import { AccountType } from 'graphql/types/AccountType';
import { AccountFormData, useAccountApis } from 'hooks/useAccountApis';
import { FilterOptions } from './components/FilterArea';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { showError } from 'utils/exceptionUtils';
import { useTranslation } from 'react-i18next';
import { SiteType } from 'graphql/types/SiteType';
import TablePaginationBar from '../../../components/TablePaginationBar';

const AccountPage: React.FC = () => {
    const { t } = useTranslation();

    const { currentWarehouse } = useWarehouseContext();
    const { createAccount, updateAccount } = useAccountApis();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<AccountFormData | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [tempFilterOptions, setTempFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const warehouse = currentWarehouse;
    const isHub = warehouse?.siteType === SiteType.HUB;

    // 查询 warehouse 下的所有操作员（包括 HUB_OPERATOR 和 WAREHOUSE_OPERATOR）
    const { data: warehouseAccountsData, loading: warehouseAccountsLoading, error: warehouseAccountsError, refetch: warehouseAccountsRefetch } = useQuery<GetAccountListingForWarehouseResponse>(GET_ACCOUNT_LISTING_FOR_WAREHOUSE, {
        variables: {
            warehouseId: warehouse?.id
        },
        skip: !warehouse?.id
    });

    // 当选择 Hub 时，额外查询 DSP 经理
    const { data: hubDspManagersData, loading: hubDspManagersLoading, error: hubDspManagersError, refetch: hubDspManagersRefetch } = useQuery<GetHubDspManagersResponse>(GET_HUB_DSP_MANAGERS, {
        variables: {
            hubId: warehouse?.id ? Number(warehouse.id) : 0
        },
        skip: !warehouse?.id || !isHub // 非 Hub 时跳过此查询
    });

    // 统一处理数据：Hub 时需要合并 HUB_OPERATOR 和 DSP_MANAGER
    const allAccounts = React.useMemo(() => {
        if (!warehouseAccountsData?.allAccounts) {
            return { allAccounts: [] };
        }
        
        if (isHub && hubDspManagersData?.getHubDspManagers) {
            // 合并 HUB_OPERATOR 和 DSP_MANAGER，去重
            const warehouseAccounts = warehouseAccountsData.allAccounts;
            const dspManagers = hubDspManagersData.getHubDspManagers;
            const allAccountsMap = new Map<number, AccountType>();
            
            // 先添加 warehouse 下的账号（HUB_OPERATOR）
            warehouseAccounts.forEach(account => {
                allAccountsMap.set(account.id, account);
            });
            
            // 再添加 DSP 经理（可能会覆盖，但 DSP_MANAGER 和 HUB_OPERATOR 不会重复）
            dspManagers.forEach(account => {
                allAccountsMap.set(account.id, account);
            });
            
            // 按 ID 降序排序
            const mergedAccounts = Array.from(allAccountsMap.values()).sort((a, b) => b.id - a.id);
            return { allAccounts: mergedAccounts };
        }
        
        return warehouseAccountsData;
    }, [warehouseAccountsData, hubDspManagersData, isHub]);
    
    const allAccountsLoading = warehouseAccountsLoading || (isHub && hubDspManagersLoading);
    const allAccountsError = warehouseAccountsError || (isHub && hubDspManagersError);

    const refetch = () => {
        warehouseAccountsRefetch();
        if (isHub) {
            hubDspManagersRefetch();
        }
    }

    // Show new account drawer
    const showNewAccountDrawer = () => {
        setSelectedAccount(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    // Show account details
    const showAccountDetail = async (account: AccountType) => {
        console.log('showAccountDetail', account);
        try {
            // Convert AccountData to AccountFormData
            const formData: AccountFormData = {
                id: account.id,
                phone: {
                    countryCode: account.countryCode,
                    phoneNumber: account.phone,
                },
                name: account.name,
                email: account.email,
                role: account.role.name,
                status: account.status,
                warehouseId: warehouse?.id ? Number(warehouse.id) : undefined,
            };

            setSelectedAccount(formData);
            setDrawerMode('edit');
            setDrawerVisible(true);
        } catch (error) {
            console.error('Error showing account detail:', error);
            showError(error);
        }
    };

    // Handle account form submission (create or update)
    const handleAccountSubmit = async (values: AccountFormData) => {
        if (drawerMode === 'create') {
            try {
                console.log('New account submitted:', values);
                const result = await createAccount(values);
                if (!result) {
                    return;
                }
                setDrawerVisible(false);
                message.success(t('wms.account.messages.personnelCreatedSuccessfully'));
                refetch();
            } catch (error) {
                console.error('Error creating account:', error);
                const code = (error as any)?.graphQLErrors?.[0]?.extensions?.code;
                if (code !== 'PERMISSION' && code !== 'UNAUTHORIZED') {
                    showError(error);
                }
            }
        } else {
            try {
                console.log('Personnel updated:', values);
                const result = await updateAccount(values);
                if (!result) {
                    return;
                }
                setDrawerVisible(false);
                message.success(t('wms.account.messages.personnelUpdatedSuccessfully'));
                refetch();
            } catch (error) {
                console.error('Error updating account:', error);
                const code = (error as any)?.graphQLErrors?.[0]?.extensions?.code;
                if (code !== 'PERMISSION' && code !== 'UNAUTHORIZED') {
                    showError(error);
                }
            }
        }
    };

    // Table column definitions
    const columns: TableColumnsType<AccountType> = [
        {
            title: t('wms.account.columns.id'),
            dataIndex: 'id',
            key: 'id',
            align: 'left' as const,
            ellipsis: false,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showAccountDetail(record)}
                    style={{ paddingLeft: 0 }}
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                </Button>
            ),
        },
        {
            title: t('wms.account.columns.name'),
            dataIndex: 'name',
            key: 'name',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('wms.account.columns.phone'),
            dataIndex: 'phone',
            key: 'phone',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('wms.account.columns.role'),
            dataIndex: ['role', 'name'],
            key: 'role.name',
            align: 'left' as const,
            ellipsis: false,
            render: (_, record) => {
                const text = t(`enum.role.${record.role.name}`);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('wms.account.columns.personnelStatus'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            ellipsis: false,
            render: (status: number) => {
                const color = status === 1 ? 'text-green-600' : 'text-gray-400';
                const text = status === 1 ? t('wms.account.status.active') : t('wms.account.status.inactive');
                return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
    ];

    const filteredData = allAccounts?.allAccounts
        .filter((account) => filterOptions.role === 'all' || filterOptions.role === account.role.name)
        .filter((account) => filterOptions.status === 2 || filterOptions.status === account.status)
        .filter((account) => filterOptions.id === '' || account.id.toString().includes(filterOptions.id))
        .filter((account) => filterOptions.name === '' || account.name.toLowerCase().includes(filterOptions.name.toLowerCase()))
        .filter((account) => filterOptions.phone === '' || account.phone.includes(filterOptions.phone)) || []

    const handleFilter = () => {
        setFilterOptions(tempFilterOptions);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setTempFilterOptions(DEFAULT_FILTER_OPTIONS);
        setFilterOptions(DEFAULT_FILTER_OPTIONS);
        setCurrentPage(1);
    };

    // 当筛选数据变化时，重置到第一页
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* Filter area */}
                <FilterArea 
                    filters={tempFilterOptions}
                    setFilters={setTempFilterOptions}
                />

                {/* 批量操作区域 */}
                <div className="flex justify-end items-center mb-3 pl-0">
                    <div className="flex items-center gap-2">
                        <Button
                            type="primary"
                            onClick={showNewAccountDrawer}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.account.buttons.new')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.account.filters.filter')}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.account.filters.reset')}</span>
                        </Button>
                        </div>
                </div>

                {/* Personnel table */}
                <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                    <Table
                        rowKey="id"
                        loading={allAccountsLoading}
                        columns={columns}
                        dataSource={filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                        pagination={false}
                        scroll={{x: 'max-content'}}
                        size="middle"
                        tableLayout="auto"
                     className="mb-4"/>
                </div>

                {/* 分页 */}
                <TablePaginationBar
                    total={filteredData.length}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    }}
                    onShowSizeChange={(_, size) => {
                        setCurrentPage(1);
                        setPageSize(size);
                    }}
                    pageSizeOptions={['10', '20', '50', '100']}
                />

                {/* Personnel form drawer (shared for create/edit) */}
                <AccountFormDrawer
                    visible={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    onSubmit={handleAccountSubmit}
                    account={selectedAccount}
                    mode={drawerMode}
                />
            </div>
    );
};

export default AccountPage;